import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cacheWorkoutsOffline } from "@/lib/offlineQueue";

interface MesocycleInfo {
  name: string;
  current_week: number;
  total_weeks: number;
  phase: string;
  periodization_type: string;
  volume_modifier: number;
  intensity_modifier: number;
  workouts_completed: number;
  workouts_planned: number;
}

interface StudentData {
  student: { id?: string; name: string; email?: string; phone?: string; photo_url: string | null };
  tenant?: { id: string; name: string; logo_url?: string | null; brand_color_hsl?: string | null } | null;
  workouts: any[];
  completed: any[];
  completed_count: number;
  latest_assessment: any;
  assessments?: any[];
  has_anamnese: boolean;
  has_pending_workouts?: boolean;
  access_token?: string;
  mesocycle?: MesocycleInfo | null;
  evolution_reports?: any[];
}

interface StudentDataContextType {
  data: StudentData | null;
  loading: boolean;
  error: string;
  offline: boolean;
  sessionExpired: boolean;
  loadData: (token: string, force?: boolean) => Promise<StudentData | null>;
  setData: (data: StudentData | null) => void;
}

const StudentDataContext = createContext<StudentDataContextType | null>(null);

const CACHE_TTL = 180_000; // 3 minutes
const STALE_TTL = 600_000; // 10 minutes — serve stale while revalidating
const OFFLINE_CACHE_KEY = "student_offline_data";
const OFFLINE_CACHE_TS_KEY = "student_offline_ts";

function saveToOfflineCache(data: StudentData) {
  try {
    localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(OFFLINE_CACHE_TS_KEY, Date.now().toString());
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function loadFromOfflineCache(): StudentData | null {
  try {
    const raw = localStorage.getItem(OFFLINE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StudentData;
  } catch {
    return null;
  }
}

function getOfflineCacheAge(): number {
  const ts = localStorage.getItem(OFFLINE_CACHE_TS_KEY);
  if (!ts) return Infinity;
  return Date.now() - parseInt(ts, 10);
}

export function StudentDataProvider({ children }: { children: ReactNode }) {
  const [data, setDataState] = useState<StudentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [offline, setOffline] = useState(!navigator.onLine);
  const [sessionExpired, setSessionExpired] = useState(false);
  const lastFetchRef = useRef<{ token: string; time: number } | null>(null);
  const inflightRef = useRef<Promise<StudentData | null> | null>(null);

  // Track online/offline status
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Detect auth session expiry (e.g. refresh token revoked)
  // Track whether user was previously signed in to avoid false "session expired" on cold start
  const wasSignedInRef = useRef(false);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[StudentDataCtx] auth event:", event, "hasSession:", !!session, "wasSignedIn:", wasSignedInRef.current);
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // User actively signed in or token refreshed — this is a confirmed valid session
        wasSignedInRef.current = true;
        setSessionExpired(false);
        setError((prev) => prev === "Sessão expirada. Faça login novamente." ? "" : prev);
      }
      if (event === "INITIAL_SESSION") {
        // Do NOT set wasSignedInRef here — the session from localStorage may have
        // a stale refresh token. If refresh fails, SIGNED_OUT will fire next.
        // Setting wasSignedInRef here would incorrectly trigger "session expired".
        setSessionExpired(false);
        setError((prev) => prev === "Sessão expirada. Faça login novamente." ? "" : prev);
      }
      if (event === "SIGNED_OUT" && wasSignedInRef.current) {
        console.log("[StudentDataCtx] SIGNED_OUT detected after being signed in — marking session expired");
        setSessionExpired(true);
        setDataState(null);
        setError("Sessão expirada. Faça login novamente.");
        localStorage.removeItem("student_token");
        localStorage.removeItem(OFFLINE_CACHE_KEY);
        localStorage.removeItem(OFFLINE_CACHE_TS_KEY);
        wasSignedInRef.current = false;
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const setData = useCallback((newData: StudentData | null) => {
    setDataState(newData);
    if (newData) {
      saveToOfflineCache(newData);
      if (newData.workouts?.length) cacheWorkoutsOffline(newData.workouts);
    }
  }, []);

  const loadData = useCallback(async (token: string, force = false): Promise<StudentData | null> => {
    const { data: authSession } = await supabase.auth.getSession();
    const userId = authSession.session?.user?.id ?? null;
    const requestKey = `${token}::${userId ?? "no-auth"}`;

    const now = Date.now();
    const cached = lastFetchRef.current;
    const age = cached ? now - cached.time : Infinity;

    // Fresh cache — return immediately
    if (!force && cached && cached.token === requestKey && age < CACHE_TTL && data) {
      return data;
    }

    // Stale cache — return data immediately but revalidate in background
    if (!force && cached && cached.token === requestKey && age < STALE_TTL && data) {
      // Fire background revalidation (no loading spinner)
      if (!inflightRef.current) {
        const bgPromise = (async () => {
          try {
            const { data: res, error: err } = await supabase.functions.invoke("student-data", {
              body: userId ? { token, user_id: userId } : { token },
            });
            if (!err && !res?.error) {
              setDataState(res);
              saveToOfflineCache(res);
              if (res.workouts?.length) cacheWorkoutsOffline(res.workouts);
              lastFetchRef.current = { token: requestKey, time: Date.now() };
            }
          } catch { /* silent bg refresh */ }
          finally { inflightRef.current = null; }
        })();
        inflightRef.current = bgPromise as any;
      }
      return data;
    }

    // Deduplicate in-flight requests (skip dedup when forcing)
    if (!force && inflightRef.current) return inflightRef.current;

    // If offline, serve from localStorage cache immediately
    if (!navigator.onLine) {
      const cached = loadFromOfflineCache();
      if (cached) {
        setDataState(cached);
        setError("");
        setOffline(true);
        return cached;
      }
      setError("Sem conexão e sem dados em cache");
      return null;
    }

    setLoading(true);
    setError("");

    const MAX_RETRIES = 2;
    const RETRY_DELAYS = [2000, 5000]; // exponential-ish backoff

    const promise = (async () => {
      let lastErr: any = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const { data: res, error: err } = await supabase.functions.invoke("student-data", {
            body: userId ? { token, user_id: userId } : { token },
          });
          if (err) throw err;
          if (res?.error) throw new Error(res.error);
          setDataState(res);
          saveToOfflineCache(res);
          if (res.workouts?.length) cacheWorkoutsOffline(res.workouts);
          lastFetchRef.current = { token: requestKey, time: Date.now() };
          return res as StudentData;
        } catch (e: any) {
          lastErr = e;
          const msg = e?.message || "Erro ao carregar dados";
          const isNotFound = msg.includes("não encontrado") || msg.includes("Acesso não encontrado") || msg.includes("Token expirado");
          const isSuspended = msg.includes("tenant_suspended") || msg.includes("suspendeu o serviço");

          // Non-retryable errors — fail immediately
          if (isSuspended) {
            localStorage.removeItem(OFFLINE_CACHE_KEY);
            localStorage.removeItem(OFFLINE_CACHE_TS_KEY);
            setDataState(null);
            setError("A academia suspendeu o serviço. Entre em contato com seu professor para mais informações.");
            return null;
          }
          if (isNotFound) {
            localStorage.removeItem(OFFLINE_CACHE_KEY);
            localStorage.removeItem(OFFLINE_CACHE_TS_KEY);
            localStorage.removeItem("student_token");
            localStorage.removeItem("student_offline_data");
            localStorage.removeItem("student_offline_ts");
            setDataState(null);
            setError("Conta não encontrada. Seu acesso pode ter sido removido.");
            // Sign out auth session so deleted student can't re-login
            supabase.auth.signOut().catch(() => {});
            setSessionExpired(true);
            return null;
          }

          // Retryable error — wait and try again
          if (attempt < MAX_RETRIES) {
            console.warn(`[StudentDataCtx] Fetch attempt ${attempt + 1} failed, retrying in ${RETRY_DELAYS[attempt]}ms...`, msg);
            await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt]));
            continue;
          }
        }
      }

      // All retries exhausted — try offline cache
      const offlineCached = loadFromOfflineCache();
      if (offlineCached) {
        setDataState(offlineCached);
        setOffline(true);
        setError("");
        return offlineCached;
      }
      setError(lastErr?.message || "Erro ao carregar dados");
      return null;
    })();

    promise.finally(() => {
      setLoading(false);
      inflightRef.current = null;
    });

    inflightRef.current = promise;
    return promise;
  }, [data]);

  return (
    <StudentDataContext.Provider value={{ data, loading, error, offline, sessionExpired, loadData, setData }}>
      {children}
    </StudentDataContext.Provider>
  );
}

export function useStudentData() {
  const ctx = useContext(StudentDataContext);
  if (!ctx) throw new Error("useStudentData must be used within StudentDataProvider");
  return ctx;
}
